import React, { useState } from 'react';
import {
  Box,
  Button,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Progress,
  Code,
  HStack,
  useToast,
  Divider,
} from '@chakra-ui/react';
import { migrateBackupPaths } from '../documentComponents/migrateBackupPaths';

const BackupPathMigration = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [isDryRun, setIsDryRun] = useState(true);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const toast = useToast();

  const handleMigration = async (dryRun) => {
    setIsRunning(true);
    setProgress(null);
    setResults(null);

    try {
      const migrationResults = await migrateBackupPaths({
        dryRun,
        batchSize: 100,
        onProgress: (progressInfo) => {
          setProgress(progressInfo);
        },
      });

      setResults(migrationResults);

      if (dryRun) {
        toast({
          title: 'Dry Run Completed',
          description: `Would update ${migrationResults.updated} records. Check console for details.`,
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Migration Completed',
          description: `Successfully updated ${migrationResults.updated} records.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Migration error:', error);
      toast({
        title: 'Migration Failed',
        description: error.message || 'An error occurred during migration.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Box p={6} borderWidth="1px" borderRadius="md" bg="white">
      <VStack spacing={4} align="stretch">
        <Text fontSize="xl" fontWeight="bold">
          Backup Path Migration
        </Text>
        
        <Alert status="info">
          <AlertIcon />
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" fontWeight="bold">
              This migration updates backup_path values to use scheduled dates from original_path.
            </Text>
            <Text fontSize="xs">
              <strong>Important:</strong> This only updates database records. Files in storage remain in their original locations.
              The system will look for files using the new paths, so you may need to move files manually if needed.
            </Text>
          </VStack>
        </Alert>

        {progress && (
          <Box>
            <Text fontSize="sm" mb={2}>
              Progress: {progress.processed} / {progress.total} processed
            </Text>
            <Progress
              value={(progress.processed / progress.total) * 100}
              colorScheme="blue"
              size="sm"
            />
            <Text fontSize="xs" color="gray.600" mt={1}>
              Updated: {progress.updated} | Skipped: {progress.skipped} | Errors: {progress.errors}
            </Text>
          </Box>
        )}

        {results && (
          <Box p={4} bg="gray.50" borderRadius="md">
            <Text fontSize="md" fontWeight="bold" mb={2}>
              Migration Results:
            </Text>
            <VStack align="start" spacing={1} fontSize="sm">
              <Text>Total Records: {results.total}</Text>
              <Text>Processed: {results.processed}</Text>
              <Text color="green.600">Updated: {results.updated}</Text>
              <Text color="gray.600">Skipped: {results.skipped}</Text>
              {results.errors > 0 && (
                <Text color="red.600">Errors: {results.errors}</Text>
              )}
              {results.dryRun && (
                <Text color="blue.600" fontStyle="italic">
                  (Dry run - no changes were made)
                </Text>
              )}
            </VStack>
            {results.errorDetails && results.errorDetails.length > 0 && (
              <Box mt={2}>
                <Text fontSize="sm" fontWeight="bold" mb={1}>
                  Error Details:
                </Text>
                {results.errorDetails.slice(0, 5).map((error, idx) => (
                  <Code key={idx} fontSize="xs" p={1} display="block" mb={1}>
                    ID {error.id}: {error.error}
                  </Code>
                ))}
                {results.errorDetails.length > 5 && (
                  <Text fontSize="xs" color="gray.600">
                    ... and {results.errorDetails.length - 5} more errors
                  </Text>
                )}
              </Box>
            )}
          </Box>
        )}

        <Divider />

        <HStack spacing={4}>
          <Button
            colorScheme="blue"
            onClick={() => handleMigration(true)}
            isLoading={isRunning}
            loadingText="Running Dry Run..."
            isDisabled={isRunning}
          >
            Run Dry Run (Test)
          </Button>
          <Button
            colorScheme="red"
            onClick={() => {
              if (window.confirm(
                'Are you sure you want to run the migration? This will update all backup_path values in the database.\n\n' +
                'It is recommended to run a dry run first to see what will be changed.'
              )) {
                handleMigration(false);
              }
            }}
            isLoading={isRunning}
            loadingText="Running Migration..."
            isDisabled={isRunning}
          >
            Run Migration (Live)
          </Button>
        </HStack>

        <Text fontSize="xs" color="gray.600">
          <strong>Recommended:</strong> Run a dry run first to preview changes, then run the live migration.
        </Text>
      </VStack>
    </Box>
  );
};

export default BackupPathMigration;

